import { IDP_CONNECT_CALLBACK } from "@/config";
import type { ClientConnectParams, GatewayCredential } from "./clientConnect";

export const IDP_CONNECT_CLIENT = "altimate-idp" as const;
export const IDP_CONNECT_PATH = "/connect";
const RETURN_KEY = "altimate.idpConnectReturn";
const RETURN_TTL = 5 * 60_000;
export type IdpConnectParams = ClientConnectParams & { client: typeof IDP_CONNECT_CLIENT };

/** A hosted callback is allowed only for this client and the exact deployed callback. */
export function readIdpConnect(
  search: URLSearchParams,
  callback = IDP_CONNECT_CALLBACK
): IdpConnectParams | null {
  if (
    !callback ||
    search.get("client") !== IDP_CONNECT_CLIENT ||
    search.get("redirect") !== callback
  )
    return null;
  for (const key of ["client", "redirect", "state"])
    if (search.getAll(key).length !== 1) return null;
  const state = search.get("state");
  if (!state || state.length > 512) return null;
  try {
    const url = new URL(callback);
    const loopback =
      url.hostname === "localhost" ||
      url.hostname.endsWith(".localhost") ||
      url.hostname === "127.0.0.1";
    if (
      (url.protocol !== "https:" && !(url.protocol === "http:" && loopback)) ||
      url.username ||
      url.password ||
      url.search ||
      url.hash ||
      url.pathname !== "/oauth2/altimate/callback"
    )
      return null;
  } catch {
    return null;
  }
  return { client: IDP_CONNECT_CLIENT, redirect: callback, state };
}

/** Deliver only a one-time token (or error), never a persistent account credential. */
export function deliverIdpConnect(
  params: IdpConnectParams,
  result: GatewayCredential | { error: string },
  callback = IDP_CONNECT_CALLBACK
): void {
  if (
    !readIdpConnect(
      new URLSearchParams({
        client: params.client,
        redirect: params.redirect,
        state: params.state,
      }),
      callback
    )
  )
    throw new Error("Invalid Dippr sign-in destination");
  const form = document.createElement("form");
  form.method = "post";
  form.action = params.redirect;
  form.dataset.altimateIdp = "true";
  form.hidden = true;
  for (const [name, value] of Object.entries({ state: params.state, ...result })) {
    // Deliberately exclude api_url and any future credential fields from this wire contract.
    if (!["state", "token", "instance", "error"].includes(name) || typeof value !== "string")
      continue;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  // Drop /connect from history first, so Back from the callback cannot mint again.
  history.replaceState(null, "", "/");
  form.submit();
}

type StashedReturn = { path: string; expires: number; awaitingSignIn: boolean };

const isConnectReturn = (path: string, callback: string | undefined): boolean => {
  const url = new URL(path, window.location.origin);
  return (
    url.origin === window.location.origin &&
    url.pathname === IDP_CONNECT_PATH &&
    !url.hash &&
    !!readIdpConnect(url.searchParams, callback)
  );
};

const writeReturn = (value: StashedReturn): void => {
  try {
    sessionStorage.setItem(RETURN_KEY, JSON.stringify(value));
  } catch {
    /* Storage unavailable: route state still supports password login. */
  }
};

/**
 * Remember a /connect destination across sign-in. `awaitingSignIn` marks a
 * return left on the way to an SSO sign-in, which the first arrival back with
 * a session (but no local user yet) keeps; see settleIdpConnectReturnOnArrival.
 */
export function stashIdpConnectReturn(
  path: string,
  callback = IDP_CONNECT_CALLBACK,
  awaitingSignIn = false
): void {
  try {
    if (!isConnectReturn(path, callback)) return;
    writeReturn({ path, expires: Date.now() + RETURN_TTL, awaitingSignIn });
  } catch {
    /* Not a URL: nothing to remember. */
  }
}
/** Stash a /connect return on the way to an SSO sign-in, for the arrival back. */
export const stashIdpConnectReturnForSignIn = (path: string): void =>
  stashIdpConnectReturn(path, IDP_CONNECT_CALLBACK, true);

export function clearIdpConnectReturn(): void {
  try {
    sessionStorage.removeItem(RETURN_KEY);
  } catch {
    /* storage unavailable */
  }
}
function readStashedReturn(callback = IDP_CONNECT_CALLBACK): StashedReturn | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (
      typeof value.path !== "string" ||
      typeof value.expires !== "number" ||
      value.expires <= Date.now() ||
      value.expires > Date.now() + RETURN_TTL ||
      !isConnectReturn(value.path, callback)
    )
      return null;
    return {
      path: value.path,
      expires: value.expires,
      awaitingSignIn: value.awaitingSignIn === true,
    };
  } catch {
    return null;
  }
}
function readIdpConnectReturn(consume: boolean, callback = IDP_CONNECT_CALLBACK): string | null {
  const stashed = readStashedReturn(callback);
  if (consume) clearIdpConnectReturn();
  return stashed?.path ?? null;
}

/**
 * SSO arrival with a session but no local user, on its way to /register,
 * whose sign-up returns to whatever is stashed (L-2):
 * - on /connect itself, that is the return;
 * - elsewhere, only a return left on the way to this sign-in survives, and
 *   only for this one arrival (SuperTokens lands on "/", not on /connect);
 * - anything else is an abandoned /connect and is dropped.
 */
export function settleIdpConnectReturnOnArrival(
  path: string,
  callback = IDP_CONNECT_CALLBACK
): void {
  const stashed = readStashedReturn(callback);
  clearIdpConnectReturn();
  let onConnect = false;
  try {
    onConnect = isConnectReturn(path, callback);
  } catch {
    /* Not a URL: not /connect. */
  }
  if (onConnect) stashIdpConnectReturn(path, callback);
  else if (stashed?.awaitingSignIn) writeReturn({ ...stashed, awaitingSignIn: false });
}

export function popIdpConnectReturn(callback = IDP_CONNECT_CALLBACK): string | null {
  return readIdpConnectReturn(true, callback);
}

/** Keep workspace switching reachable while the protected connect route waits for sign-in. */
export function getIdpWorkspaceSwitchURL(callback = IDP_CONNECT_CALLBACK): string | null {
  const path = readIdpConnectReturn(false, callback);
  if (!path) return null;
  const params = readIdpConnect(new URL(path, window.location.origin).searchParams, callback);
  if (!params) return null;
  const url = new URL("/oauth2/altimate/switch", params.redirect);
  url.searchParams.set("state", params.state);
  return url.toString();
}
